import axios from 'axios';
import api from '@/api/api.js';
import photoUploadSessionStore from '@/services/photo-upload-session-store.js';
import { v4 as uuidv4 } from 'uuid';

export default {
  data() {
    return {
      uuidValue: '',
      progress: 0,
      isProgress: false,
      imageCount: 0,
      originalImageCount: 0,
      isNotified: false,
      rateLimitNotificationTimer: null,
      completionResetTimer: null,
      uploadConcurrency: 2,
      mainQueueMaxAttempts: 2,
      deferredRetryRounds: 5,
      baseRetryDelayMs: 1000,
      maxRetryDelayMs: 30000,
      uploadStallWarningMs: 30000,
      uploadStallAbortMs: 180000,
      serverWaitWarningMs: 60000,
      uploadBatches: [],
      deferredBatches: [],
      uploadTotalBytes: 0,
      networkTransferredBytes: 0,
      speedCheckpointBytes: 0,
      speedCheckpointAt: 0,
      uploadSpeedBps: 0,
      uploadStatus: 'idle',
      uploadPhase: 'idle',
      confirmedPhotosCount: 0,
      isUploadRunning: false,
      isUploadStarting: false,
      uploadAbortRequested: false,
      uploadPaused: false,
      uploadExitMode: null,
    };
  },
  computed: {
    activeUploadRequests() {
      return this.uploadBatches.filter((batch) =>
        ['uploading', 'stalled', 'waiting', 'waiting-long'].includes(batch.status)
      ).length;
    },
    pendingPhotosCount() {
      const deferredIds = new Set(this.deferredBatches.map((batch) => batch.id));
      return this.uploadBatches.reduce((count, batch) => {
        if (deferredIds.has(batch.id)) {
          return count;
        }
        return count + batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        ).length;
      }, 0);
    },
    deferredPhotosCount() {
      const deferredIds = new Set(this.deferredBatches.map((batch) => batch.id));
      return this.uploadBatches.reduce((count, batch) => {
        if (!deferredIds.has(batch.id) && batch.status !== 'deferred') {
          return count;
        }
        return count + batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        ).length;
      }, 0);
    },
    failedPhotosCount() {
      return this.uploadBatches.reduce(
        (count, batch) => count + batch.entries.filter(
          (entry) => !entry.confirmed && entry.permanentFailed
        ).length,
        0
      );
    },
    displayUploadedBytes() {
      return Math.round(Math.max(0, Math.min(1, this.progress)) * this.uploadTotalBytes);
    },
  },
  created() {
    this.generateUniqueId();
  },
  mounted() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    window.addEventListener('offline', this.handleBrowserOffline);
    window.addEventListener('online', this.handleBrowserOnline);
  },
  beforeDestroy() {
    this.cleanupPhotoUploadManager();
  },
  beforeUnmount() {
    this.cleanupPhotoUploadManager();
  },
  methods: {
    generateUniqueId() {
      this.uuidValue = uuidv4();
    },
    clearAllData() {
      this.uploadAbortRequested = true;
      this.cancelActiveUploads('UPLOAD_RESET', true);
      this.generateUniqueId();
      this.selectedImage = [];
      this.largeUploadNoticeShown = false;
      this.isProgress = false;
      this.progress = 0;
      this.imageCount = 0;
      this.originalImageCount = 0;
      this.uploadBatches = [];
      this.deferredBatches = [];
      this.uploadTotalBytes = 0;
      this.preconfirmedBytes = 0;
      this.networkTransferredBytes = 0;
      this.speedCheckpointBytes = 0;
      this.speedCheckpointAt = 0;
      this.uploadSpeedBps = 0;
      this.uploadStatus = 'idle';
      this.uploadPhase = 'idle';
      this.confirmedPhotosCount = 0;
      this.isUploadRunning = false;
      this.isUploadStarting = false;
      this.uploadPaused = false;
      this.uploadExitMode = null;
      this.uploadAbortRequested = false;
      if (this.$refs.FilePickerRef) {
        this.$refs.FilePickerRef.addFromParent(this.selectedImage);
      }
    },
    beforeUnloadHandler(e) {
      // Protect only an upload which is actually in progress. A session already
      // saved via "Resume later" is persisted in IndexedDB and is safe to leave.
      if (this.isUploadRunning) {
        e.preventDefault();
        // Modern browsers use preventDefault(); returnValue is kept for legacy support.
        e.returnValue = true;
        return '';
      }
      return undefined;
    },
    splitToChunks(array, chunkSize = 5) {
      const result = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        result.push(array.slice(i, i + chunkSize));
      }
      return result;
    },
    getWordpressUploadToken() {
      const wpToken = this.user?.jwt_token ?? null;

      if (wpToken) {
        return wpToken;
      }

      this.$store.dispatch('auth/logout');
      this.$q.notify({
        icon: 'warning',
        color: 'negative',
        message: this.$t('error.badAuthToken'),
        position: 'top-right',
      });

      return null;
    },
    createUploadMetadata(wpToken) {
      return {
        uploadId: this.uuidValue,
        cityId: this.selectedEvent.assign_city,
        countryId: this.selectedCountry.id,
        runId: this.selectedEvent.id,
        authorId: this.user.id,
        photosCount: this.originalImageCount,
        wpToken,
      };
    },
    createUploadBatches(files) {
      return this.splitToChunks(files).map((batchFiles, batchIndex) => {
        const entries = batchFiles.map((file, fileIndex) => {
          const clientFileId = file.__runpixieClientFileId || uuidv4();
          this.decorateResumeFile(file, clientFileId, this.getFileRelativePath(file));
          return {
            id: clientFileId || `${batchIndex}-${fileIndex}-${uuidv4()}`,
            clientFileId,
            file,
            confirmed: false,
            permanentFailed: false,
            lastError: null,
          };
        });

        return {
          id: `${this.uuidValue}-${batchIndex}-${uuidv4()}`,
          index: batchIndex,
          entries,
          totalBytes: entries.reduce((sum, entry) => sum + entry.file.size, 0),
          progressBytes: 0,
          attemptLoadedBytes: 0,
          attemptTotalBytes: 0,
          attempts: 0,
          mainAttempts: 0,
          deferredRounds: 0,
          status: 'pending',
          splitToSingles: false,
          deferredReason: null,
          lastProgressAt: null,
          waitingSince: null,
          cancelSource: null,
          lastError: null,
        };
      });
    },
    buildBatchFormData(metadata, batch, entries) {
      const data = new FormData();
      data.append('upload_id', metadata.uploadId);
      data.append('wp_city_id', metadata.cityId);
      data.append('wp_country_id', metadata.countryId);
      data.append('wp_run_id', metadata.runId);
      data.append('wp_author_id', metadata.authorId);
      data.append('photos_count', metadata.photosCount);

      // Existing correlation metadata; the resume feature itself does not require backend support.
      data.append('client_batch_id', batch.id);
      data.append('client_attempt', batch.attempts);

      entries.forEach((entry) => {
        data.append('photos[]', entry.file);
      });

      return data;
    },
    resetUploadMetrics(files, preserveOriginalCount = false, manifest = null) {
      this.progress = 0;
      this.uploadStatus = 'preparing';
      this.uploadPhase = 'main';
      this.uploadSpeedBps = 0;
      this.networkTransferredBytes = 0;
      this.speedCheckpointBytes = 0;
      this.speedCheckpointAt = Date.now();
      this.confirmedPhotosCount = manifest ? this.getManifestConfirmedCount(manifest) : 0;
      this.preconfirmedBytes = manifest ? this.getManifestConfirmedBytes(manifest) : 0;
      this.imageCount = manifest
        ? Number(manifest.totalFiles || manifest.files?.length || files.length)
        : files.length;
      this.deferredBatches = [];

      if (!preserveOriginalCount) {
        this.originalImageCount = this.imageCount;
      }
    },
    async startPhotoUpload(files, preserveOriginalCount = false, resumeManifest = null) {
      if (this.isUploadRunning || this.isUploadStarting || !files.length) {
        return;
      }

      this.isUploadStarting = true;
      const wpToken = this.getWordpressUploadToken();
      if (!wpToken) {
        this.isUploadStarting = false;
        return;
      }

      const isResume = Boolean(resumeManifest);
      try {
        this.uploadAbortRequested = false;
        this.uploadPaused = false;
        this.uploadExitMode = null;
        this.telemetryBuffer = [];
        this.telemetryHistory = [];

        if (isResume) {
          this.restoreUploadContextFromManifest(resumeManifest);
          resumeManifest.status = 'active';
          await this.persistCurrentUploadManifest('active');
        } else {
          this.telemetrySessionId = uuidv4();
          resumeManifest = await this.createFreshUploadManifest(files);
        }

        this.resetUploadMetrics(files, preserveOriginalCount, resumeManifest);
        this.uploadBatches = this.createUploadBatches(files);
        this.uploadTotalBytes = Number(resumeManifest?.totalBytes || 0) ||
          this.uploadBatches.reduce((sum, batch) => sum + batch.totalBytes, 0);
        this.isProgress = true;
        this.isUploadRunning = true;
        this.refreshUploadMetrics();
        this.refreshUploadStatus();
      } finally {
        this.isUploadStarting = false;
      }

      const metadata = this.createUploadMetadata(wpToken);
      this.logUploadEvent(isResume ? 'upload.session.resumed' : 'upload.session.started', {
        files_count: this.imageCount,
        remaining_files: files.length,
        total_bytes: this.uploadTotalBytes,
        concurrency: this.uploadConcurrency,
        main_attempts: this.mainQueueMaxAttempts,
        deferred_rounds: this.deferredRetryRounds,
      });

      try {
        await this.processUploadQueue(metadata);
      } catch (error) {
        console.error('[photo-upload] Unexpected upload manager error', error);
        this.logUploadEvent('upload.manager.error', {
          error: this.serializeUploadError(error),
        });
        this.uploadBatches.forEach((batch) => {
          if (batch.status !== 'completed') {
            this.markEntriesPermanentFailed(
              batch.entries.filter((entry) => !entry.confirmed),
              error,
              'manager_error'
            );
            batch.status = 'failed';
            batch.lastError = error;
          }
        });
      } finally {
        this.isUploadRunning = false;
      }

      if (this.uploadExitMode === 'later') {
        await this.finalizeSendLater();
        return;
      }

      const failedFiles = this.getUnconfirmedFiles();
      if (failedFiles.length > 0) {
        this.selectedImage = failedFiles;
        this.uploadStatus = 'failed';
        this.uploadSpeedBps = 0;
        this.isProgress = false;
        this.logUploadEvent('upload.session.failed', {
          failed_files: failedFiles.length,
          confirmed_files: this.confirmedPhotosCount,
          total_bytes: this.uploadTotalBytes,
          network_bytes: this.networkTransferredBytes,
        });
        this.scheduleTelemetryFlush(true);
        await this.persistCurrentUploadManifest('failed');
        this.showFailedUploadNotification();
        return;
      }

      this.progress = 1;
      this.uploadStatus = 'completed';
      this.uploadPhase = 'completed';
      this.uploadSpeedBps = 0;
      this.logUploadEvent('upload.session.completed', {
        files_count: this.imageCount,
        total_bytes: this.uploadTotalBytes,
        network_bytes: this.networkTransferredBytes,
      });
      this.scheduleTelemetryFlush(true);
      if (this._uploadManifest?.uploadId) {
        try {
          await photoUploadSessionStore.deleteSession(this._uploadManifest.uploadId);
        } catch (error) {
          console.warn('[photo-upload] Could not remove completed upload manifest', error);
        }
      }
      this._uploadManifest = null;
      this._manifestFilesById = null;
      this._pendingResumeManifest = null;
      this.pendingResumeSummary = null;
      this.resumeAwaitingReselection = false;
      this._selectedDirectoryHandle = null;

      if (this.completionResetTimer) {
        clearTimeout(this.completionResetTimer);
      }
      this.completionResetTimer = setTimeout(() => {
        this.completionResetTimer = null;
        this.$q.notify({
          icon: 'done',
          color: 'positive',
          message: this.$t('success.imageAdded'),
          timeout: 0,
          actions: [{ icon: 'close', color: 'white' }],
          position: 'top-right',
        });
        this.clearAllData();
        this.step = 1;
      }, 1200);
    },
    async processUploadQueue(metadata) {
      this.uploadPhase = 'main';
      await this.processBatchesWithConcurrency(
        this.uploadBatches,
        (batch) => this.processMainBatch(metadata, batch)
      );

      if (this.uploadAbortRequested) {
        return;
      }

      this.uploadPhase = 'deferred';
      for (let round = 1; round <= this.deferredRetryRounds; round += 1) {
        if (this.uploadAbortRequested) {
          return;
        }

        this.deferredBatches = this.deferredBatches.filter((batch) =>
          this.hasRetryablePending(batch)
        );

        if (!this.deferredBatches.length) {
          break;
        }

        this.logUploadEvent('upload.deferred.round.started', {
          round,
          batches: this.deferredBatches.length,
          photos: this.countRetryableEntries(this.deferredBatches),
        });

        const roundBatches = [...this.deferredBatches];
        await this.processBatchesWithConcurrency(
          roundBatches,
          (batch) => this.processDeferredBatch(metadata, batch, round)
        );

        this.deferredBatches = this.deferredBatches.filter((batch) =>
          this.hasRetryablePending(batch)
        );

        if (this.deferredBatches.length && round < this.deferredRetryRounds) {
          await this.delayWithControl(Math.min(5000, 1000 * round));
        }
      }

      if (this.uploadAbortRequested) {
        return;
      }

      this.deferredBatches.forEach((batch) => {
        const remaining = batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        );
        if (remaining.length) {
          const error = new Error('DEFERRED_RETRIES_EXHAUSTED');
          this.markEntriesPermanentFailed(remaining, error, 'retry_exhausted');
          batch.status = 'failed';
          batch.lastError = error;
          this.logUploadEvent('upload.batch.retry_exhausted', {
            batch_id: batch.id,
            remaining_files: remaining.length,
            attempts: batch.attempts,
          });
        }
      });

      this.refreshUploadStatus();
    },
    async processBatchesWithConcurrency(batches, handler) {
      let nextBatchIndex = 0;
      const workerCount = Math.min(this.uploadConcurrency, batches.length);

      const worker = async () => {
        while (nextBatchIndex < batches.length && !this.uploadAbortRequested) {
          await this.waitWhilePaused();
          if (this.uploadAbortRequested) {
            return;
          }

          const currentIndex = nextBatchIndex;
          nextBatchIndex += 1;
          await handler(batches[currentIndex]);
        }
      };

      await Promise.all(Array.from({ length: workerCount }, () => worker()));
    },
    async processMainBatch(metadata, batch) {
      while (
        batch.mainAttempts < this.mainQueueMaxAttempts &&
        !this.uploadAbortRequested &&
        this.hasRetryablePending(batch)
      ) {
        await this.waitWhilePaused();
        if (this.uploadAbortRequested) {
          return;
        }

        await this.ensureOnline(batch);
        if (this.uploadAbortRequested) {
          return;
        }

        const pendingEntries = batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        );
        if (!pendingEntries.length) {
          break;
        }

        batch.mainAttempts += 1;
        batch.attempts += 1;
        batch.lastError = null;

        try {
          const response = await this.sendBatchAttempt(metadata, batch, pendingEntries);
          this.applyBatchResponse(batch, pendingEntries, response);

          if (!this.hasRetryablePending(batch)) {
            batch.status = this.hasPermanentFailures(batch) ? 'failed' : 'completed';
            this.refreshUploadStatus();
            return;
          }

          const partialError = new Error('PARTIAL_UPLOAD');
          partialError.isPartialUpload = true;
          throw partialError;
        } catch (error) {
          this.prepareBatchAfterError(batch, error);

          if (this.uploadAbortRequested || this.isUploadAbortError(error)) {
            return;
          }

          if (this.shouldSplitBatch(error, pendingEntries)) {
            batch.splitToSingles = true;
            this.deferBatch(batch, error, 'validation_split');
            return;
          }

          if (!this.isRetryableUploadError(error)) {
            this.markEntriesPermanentFailed(pendingEntries, error, 'permanent_http_error');
            batch.status = 'failed';
            this.refreshUploadStatus();
            return;
          }

          if (!navigator.onLine || this.isOfflineUploadError(error)) {
            batch.mainAttempts = Math.max(0, batch.mainAttempts - 1);
            batch.status = 'offline';
            this.refreshUploadStatus();
            await this.waitUntilOnline();
            continue;
          }

          if (batch.mainAttempts >= this.mainQueueMaxAttempts) {
            this.deferBatch(batch, error, 'main_attempts_exhausted');
            return;
          }

          const retryDelay = Math.min(3000, this.getRetryDelayMs(error, batch.attempts));
          if (error.response && error.response.status === 429) {
            this.showRateLimitNotification(retryDelay);
          }
          batch.status = 'retrying';
          this.refreshUploadStatus();
          this.logUploadEvent('upload.batch.retry_scheduled', {
            batch_id: batch.id,
            phase: 'main',
            delay_ms: retryDelay,
            attempt: batch.attempts,
            error: this.serializeUploadError(error),
          });
          await this.delayWithControl(retryDelay);
        }
      }

      if (this.hasRetryablePending(batch) && !this.uploadAbortRequested) {
        this.deferBatch(batch, batch.lastError, 'main_finished_with_pending');
      }
    },
    async processDeferredBatch(metadata, batch, round) {
      await this.waitWhilePaused();
      if (this.uploadAbortRequested || !this.hasRetryablePending(batch)) {
        return;
      }

      batch.deferredRounds = round;
      batch.status = 'deferred';
      this.refreshUploadStatus();

      if (batch.splitToSingles) {
        const entries = batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        );

        for (const entry of entries) {
          if (this.uploadAbortRequested) {
            return;
          }
          await this.processDeferredAttempt(metadata, batch, [entry], round);
        }
      } else {
        const entries = batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        );
        await this.processDeferredAttempt(metadata, batch, entries, round);
      }

      if (!this.hasRetryablePending(batch)) {
        batch.status = this.hasPermanentFailures(batch) ? 'failed' : 'completed';
      } else {
        batch.status = 'deferred';
      }
      this.refreshUploadStatus();
    },
    async processDeferredAttempt(metadata, batch, entries, round) {
      if (!entries.length || this.uploadAbortRequested) {
        return;
      }

      await this.waitWhilePaused();
      await this.ensureOnline(batch);
      if (this.uploadAbortRequested) {
        return;
      }

      batch.attempts += 1;
      batch.lastError = null;

      try {
        const response = await this.sendBatchAttempt(metadata, batch, entries);
        this.applyBatchResponse(batch, entries, response);

        const stillPending = entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        );
        if (stillPending.length) {
          const partialError = new Error('PARTIAL_UPLOAD');
          partialError.isPartialUpload = true;
          throw partialError;
        }
      } catch (error) {
        this.prepareBatchAfterError(batch, error);

        if (this.uploadAbortRequested || this.isUploadAbortError(error)) {
          return;
        }

        if (this.shouldSplitBatch(error, entries)) {
          batch.splitToSingles = true;
          this.logUploadEvent('upload.batch.split_to_singles', {
            batch_id: batch.id,
            round,
            files: entries.length,
            error: this.serializeUploadError(error),
          });
          return;
        }

        if (!this.isRetryableUploadError(error)) {
          this.markEntriesPermanentFailed(entries, error, 'permanent_http_error');
          return;
        }

        if (!navigator.onLine || this.isOfflineUploadError(error)) {
          batch.status = 'offline';
          this.refreshUploadStatus();
          await this.waitUntilOnline();
          return;
        }

        const retryDelay = this.getRetryDelayMs(error, batch.attempts);
        if (error.response && error.response.status === 429) {
          this.showRateLimitNotification(retryDelay);
        }
        this.logUploadEvent('upload.batch.deferred_retry_wait', {
          batch_id: batch.id,
          round,
          delay_ms: retryDelay,
          error: this.serializeUploadError(error),
        });
        await this.delayWithControl(retryDelay);
      }
    },
    async sendBatchAttempt(metadata, batch, entries) {
      const formData = this.buildBatchFormData(metadata, batch, entries);
      const cancelSource = axios.CancelToken.source();
      const attemptTotalBytes = entries.reduce(
        (sum, entry) => sum + entry.file.size,
        0
      );
      const confirmedBytesAtAttemptStart = this.getBatchConfirmedBytes(batch);
      const attemptStartedAt = Date.now();
      let bodySentAt = null;
      let stallLogged = false;
      let waitingLongLogged = false;

      batch.cancelSource = cancelSource;
      batch.attemptLoadedBytes = 0;
      batch.attemptTotalBytes = attemptTotalBytes;
      batch.lastProgressAt = Date.now();
      batch.waitingSince = null;
      batch.status = 'uploading';
      this.refreshUploadStatus();
      this.logUploadEvent('upload.batch.started', {
        batch_id: batch.id,
        phase: this.uploadPhase,
        attempt: batch.attempts,
        files_count: entries.length,
        bytes: attemptTotalBytes,
        single_mode: entries.length === 1 && batch.splitToSingles,
      });

      const monitor = setInterval(() => {
        const now = Date.now();

        if (['uploading', 'stalled'].includes(batch.status)) {
          const idleFor = now - batch.lastProgressAt;

          if (idleFor >= this.uploadStallAbortMs) {
            this.logUploadEvent('upload.stall.aborted', {
              batch_id: batch.id,
              idle_ms: idleFor,
              attempt: batch.attempts,
            });
            cancelSource.cancel('UPLOAD_STALLED');
            return;
          }

          if (idleFor >= this.uploadStallWarningMs && batch.status !== 'stalled') {
            batch.status = 'stalled';
            if (!stallLogged) {
              stallLogged = true;
              this.logUploadEvent('upload.stall.detected', {
                batch_id: batch.id,
                idle_ms: idleFor,
                attempt: batch.attempts,
              });
            }
            this.refreshUploadStatus();
          }
        }

        if (
          batch.status === 'waiting' &&
          batch.waitingSince &&
          now - batch.waitingSince >= this.serverWaitWarningMs
        ) {
          batch.status = 'waiting-long';
          if (!waitingLongLogged) {
            waitingLongLogged = true;
            this.logUploadEvent('upload.server_wait.long', {
              batch_id: batch.id,
              waiting_ms: now - batch.waitingSince,
              attempt: batch.attempts,
            });
          }
          this.refreshUploadStatus();
        }
      }, 5000);

      try {
        const response = await api.sendPhotoData(formData, metadata.wpToken, {
          cancelToken: cancelSource.token,
          onUploadProgress: (event) => {
            const now = Date.now();
            const eventTotal = Number(event.total) || attemptTotalBytes;
            const eventLoaded = Number(event.loaded) || 0;
            const ratio = eventTotal > 0
              ? Math.min(1, eventLoaded / eventTotal)
              : 0;
            const equivalentLoaded = Math.min(
              attemptTotalBytes,
              Math.round(attemptTotalBytes * ratio)
            );

            if (equivalentLoaded < batch.attemptLoadedBytes) {
              batch.attemptLoadedBytes = 0;
            }

            const delta = Math.max(0, equivalentLoaded - batch.attemptLoadedBytes);
            if (delta > 0) {
              batch.lastProgressAt = now;
            }
            batch.attemptLoadedBytes = equivalentLoaded;
            this.networkTransferredBytes += delta;
            batch.progressBytes = Math.max(
              batch.progressBytes,
              confirmedBytesAtAttemptStart + equivalentLoaded
            );

            if (ratio >= 0.9999) {
              batch.progressBytes = Math.max(
                batch.progressBytes,
                confirmedBytesAtAttemptStart + attemptTotalBytes
              );
              if (!batch.waitingSince) {
                batch.waitingSince = now;
                bodySentAt = now;
                this.logUploadEvent('upload.batch.body_sent', {
                  batch_id: batch.id,
                  attempt: batch.attempts,
                  upload_ms: now - attemptStartedAt,
                  bytes: attemptTotalBytes,
                });
              }
              batch.status = 'waiting';
            } else {
              batch.waitingSince = null;
              batch.status = 'uploading';
            }

            this.refreshUploadMetrics();
            this.refreshUploadStatus();
          },
        });

        batch.progressBytes = batch.totalBytes;
        this.refreshUploadMetrics();
        this.logUploadEvent('upload.batch.response', {
          batch_id: batch.id,
          attempt: batch.attempts,
          http_status: response?.status || null,
          duration_ms: Date.now() - attemptStartedAt,
          server_wait_ms: bodySentAt ? Date.now() - bodySentAt : null,
          request_id: this.getResponseRequestId(response),
        });
        return response;
      } catch (error) {
        const serializedError = this.serializeUploadError(error);
        this.logUploadEvent('upload.batch.error', {
          batch_id: batch.id,
          attempt: batch.attempts,
          duration_ms: Date.now() - attemptStartedAt,
          server_wait_ms: bodySentAt ? Date.now() - bodySentAt : null,
          http_status: serializedError?.http_status ?? null,
          request_id: serializedError?.request_id ?? null,
          error: serializedError,
        });
        throw error;
      } finally {
        clearInterval(monitor);
        batch.cancelSource = null;
      }
    },
    applyBatchResponse(batch, attemptedEntries, response) {
      const uploadedImages = response?.data?.data || [];
      const successCounts = uploadedImages
        .filter((item) => item.message === 'photoUpload.image.added')
        .reduce((result, item) => {
          result[item.photo] = (result[item.photo] || 0) + 1;
          return result;
        }, {});

      attemptedEntries.forEach((entry) => {
        const name = entry.file.name;
        if (!entry.confirmed && successCounts[name] > 0) {
          entry.confirmed = true;
          entry.permanentFailed = false;
          entry.lastError = null;
          successCounts[name] -= 1;
          this.confirmedPhotosCount += 1;
          // clientFileId is local-only in this frontend-only release.
          this.markManifestFileConfirmed(entry.clientFileId);
        }
      });

      batch.progressBytes = this.getBatchConfirmedBytes(batch);
      this.refreshUploadMetrics();
    },
    prepareBatchAfterError(batch, error) {
      batch.lastError = error;
      batch.progressBytes = this.getBatchConfirmedBytes(batch);
      batch.attemptLoadedBytes = 0;
      batch.attemptTotalBytes = 0;
      this.refreshUploadMetrics();
    },
    getBatchConfirmedBytes(batch) {
      return batch.entries.reduce(
        (sum, entry) => sum + (entry.confirmed ? entry.file.size : 0),
        0
      );
    },
    refreshUploadMetrics() {
      if (!this.uploadTotalBytes) {
        this.progress = 0;
        return;
      }

      const completedBytes = this.preconfirmedBytes + this.uploadBatches.reduce(
        (sum, batch) => sum + Math.min(batch.progressBytes, batch.totalBytes),
        0
      );
      this.progress = Math.min(1, completedBytes / this.uploadTotalBytes);

      const now = Date.now();
      const elapsed = now - this.speedCheckpointAt;
      if (elapsed >= 750) {
        const transferred = this.networkTransferredBytes - this.speedCheckpointBytes;
        const instantaneous = Math.max(0, (transferred * 1000) / elapsed);
        this.uploadSpeedBps = this.uploadSpeedBps > 0
          ? (this.uploadSpeedBps * 0.65) + (instantaneous * 0.35)
          : instantaneous;
        this.speedCheckpointBytes = this.networkTransferredBytes;
        this.speedCheckpointAt = now;
      }
    },
    refreshUploadStatus() {
      if (this.uploadExitMode === 'later' && this.uploadAbortRequested) {
        this.uploadStatus = 'stopping';
        this.uploadSpeedBps = 0;
        return;
      }

      if (this.uploadPaused) {
        this.uploadStatus = this.activeUploadRequests > 0 ? 'pausing' : 'paused';
        if (this.activeUploadRequests === 0) {
          this.uploadSpeedBps = 0;
        }
        return;
      }

      const statuses = this.uploadBatches.map((batch) => batch.status);

      // Surface degraded concurrent batches before a healthy uploading batch so
      // one active request does not hide another request that is stalled/offline
      // or has been waiting on the server for too long.
      if (statuses.includes('stalled')) {
        this.uploadStatus = 'stalled';
        return;
      }
      if (statuses.includes('waiting-long')) {
        this.uploadStatus = 'waiting-long';
        return;
      }
      if (statuses.includes('offline')) {
        this.uploadStatus = 'offline';
        this.uploadSpeedBps = 0;
        return;
      }
      if (statuses.includes('uploading')) {
        this.uploadStatus = 'uploading';
        return;
      }
      if (statuses.includes('waiting')) {
        this.uploadStatus = 'waiting';
        return;
      }
      if (statuses.includes('retrying')) {
        this.uploadStatus = 'retrying';
        return;
      }
      if (statuses.includes('deferred')) {
        this.uploadStatus = 'deferred';
        return;
      }
      if (statuses.includes('pending')) {
        this.uploadStatus = 'preparing';
        return;
      }
      if (statuses.includes('failed')) {
        this.uploadStatus = 'failed';
        this.uploadSpeedBps = 0;
        return;
      }
      if (statuses.length && statuses.every((status) => status === 'completed')) {
        this.uploadStatus = 'completed';
        this.uploadSpeedBps = 0;
      }
    },
    pauseUpload() {
      if (!this.isUploadRunning || this.uploadPaused || this.uploadAbortRequested) {
        return;
      }
      this.uploadPaused = true;
      this.logUploadEvent('upload.paused', {
        active_requests: this.activeUploadRequests,
        confirmed_files: this.confirmedPhotosCount,
      });
      this.$q.notify({
        icon: 'pause',
        color: 'info',
        timeout: 3500,
        message: this.$t('addMedia.guide.pausedNotice'),
        position: 'top-right',
      });
      this.refreshUploadStatus();
    },
    resumeUpload() {
      if (!this.isUploadRunning || !this.uploadPaused || this.uploadAbortRequested) {
        return;
      }
      this.uploadPaused = false;
      this.logUploadEvent('upload.resumed', {
        confirmed_files: this.confirmedPhotosCount,
      });
      this.$q.notify({
        icon: 'play_arrow',
        color: 'positive',
        timeout: 3000,
        message: this.$t('addMedia.guide.continuedNotice'),
        position: 'top-right',
      });
      this.refreshUploadStatus();
    },
    sendLaterUpload() {
      if (!this.isUploadRunning) {
        return;
      }

      this.uploadExitMode = 'later';
      this.uploadAbortRequested = true;
      this.uploadPaused = false;
      this.logUploadEvent('upload.send_later.requested', {
        active_requests: this.activeUploadRequests,
        remaining_files: this.getUnconfirmedFiles().length,
      });

      // Uploading bodies can be cancelled safely. Requests which already sent 100%
      // and are waiting for Laravel are allowed to finish to avoid duplicate uploads.
      this.cancelActiveUploads('UPLOAD_SEND_LATER', false);
      this.refreshUploadStatus();
    },
    async finalizeSendLater() {
      const remainingFiles = this.getUnconfirmedFiles();
      this.selectedImage = remainingFiles;
      if (this.$refs.FilePickerRef) {
        this.$refs.FilePickerRef.addFromParent(remainingFiles);
      }
      this.logUploadEvent('upload.session.saved_for_later', {
        remaining_files: remainingFiles.length,
        confirmed_files: this.confirmedPhotosCount,
        total_bytes: this.uploadTotalBytes,
        network_bytes: this.networkTransferredBytes,
      });
      this.scheduleTelemetryFlush(true);
      await this.persistCurrentUploadManifest('saved_for_later');

      // Keep the legacy same-tab behaviour: remaining files stay in the picker
      // and the user can simply continue later. The persistent resume banner is
      // only reconstructed after an actual reload / revisit via
      // detectPendingUploadManifest().
      this._pendingResumeManifest = null;
      this.pendingResumeSummary = null;
      this.resumeAwaitingReselection = false;

      this.step = 1;
      this.isProgress = false;
      this.progress = 0;
      this.uploadStatus = 'idle';
      this.uploadPhase = 'idle';
      this.uploadSpeedBps = 0;
      this.uploadPaused = false;
      this.uploadAbortRequested = false;
      this.uploadExitMode = null;
      this.uploadBatches = [];
      this.deferredBatches = [];
      this.uploadTotalBytes = 0;

      this.$q.notify({
        icon: 'save',
        color: 'info',
        message: this.$t('addMedia.guide.savedLaterNotice', {
          confirmed: this.confirmedPhotosCount,
          total: this.originalImageCount || this.imageCount,
          remaining: remainingFiles.length,
        }),
        timeout: 7000,
        position: 'top-right',
      });
    },
    deferBatch(batch, error, reason) {
      batch.status = 'deferred';
      batch.deferredReason = reason;
      batch.lastError = error || batch.lastError;
      if (!this.deferredBatches.some((item) => item.id === batch.id)) {
        this.deferredBatches.push(batch);
      }
      this.logUploadEvent('upload.batch.deferred', {
        batch_id: batch.id,
        reason,
        pending_files: batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        ).length,
        attempts: batch.attempts,
        error: this.serializeUploadError(error),
      });
      this.refreshUploadStatus();
    },
    shouldSplitBatch(error, entries) {
      const status = error?.response?.status;
      return entries.length > 1 && (status === 413 || status === 422);
    },
    markEntriesPermanentFailed(entries, error, reason) {
      entries.forEach((entry) => {
        if (!entry.confirmed) {
          entry.permanentFailed = true;
          entry.lastError = error || null;
          this.markManifestFileFailed(entry.clientFileId);
        }
      });
      this.logUploadEvent('upload.files.permanent_failed', {
        files_count: entries.length,
        reason,
        file_meta: entries.slice(0, 10).map((entry) => ({
          size: entry.file.size,
          type: entry.file.type || null,
          extension: this.getFileExtension(entry.file.name),
        })),
        error: this.serializeUploadError(error),
      });
    },
    hasRetryablePending(batch) {
      return batch.entries.some(
        (entry) => !entry.confirmed && !entry.permanentFailed
      );
    },
    hasPermanentFailures(batch) {
      return batch.entries.some(
        (entry) => !entry.confirmed && entry.permanentFailed
      );
    },
    countRetryableEntries(batches) {
      return batches.reduce(
        (count, batch) => count + batch.entries.filter(
          (entry) => !entry.confirmed && !entry.permanentFailed
        ).length,
        0
      );
    },
    getUnconfirmedFiles() {
      return this.uploadBatches.flatMap((batch) =>
        batch.entries
          .filter((entry) => !entry.confirmed)
          .map((entry) => entry.file)
      );
    },
    getFileExtension(name) {
      const parts = String(name || '').split('.');
      return parts.length > 1 ? parts.pop().toLowerCase() : '';
    },
    isUploadAbortError(error) {
      return axios.isCancel(error) && [
        'UPLOAD_CANCELLED',
        'UPLOAD_RESET',
        'UPLOAD_SEND_LATER',
      ].includes(error.message);
    },
    isOfflineUploadError(error) {
      return axios.isCancel(error) && error.message === 'NETWORK_OFFLINE';
    },
    isRetryableUploadError(error) {
      if (error?.isPartialUpload) {
        return true;
      }
      if (axios.isCancel(error)) {
        return ['UPLOAD_STALLED', 'NETWORK_OFFLINE'].includes(error.message);
      }
      if (!error?.response) {
        return true;
      }

      const status = error.response.status;
      return status === 408 || status === 425 || status === 429 || status >= 500;
    },
    getRetryDelayMs(error, attempt) {
      const retryAfter = this.parseRetryAfter(error?.response?.headers?.['retry-after']);
      if (retryAfter !== null) {
        return retryAfter;
      }

      const exponential = Math.min(
        this.maxRetryDelayMs,
        this.baseRetryDelayMs * Math.pow(2, Math.max(0, attempt - 1))
      );
      const jitter = Math.floor(Math.random() * Math.min(1000, exponential * 0.25));
      return exponential + jitter;
    },
    parseRetryAfter(value) {
      if (value === undefined || value === null || value === '') {
        return null;
      }

      const seconds = Number(value);
      if (Number.isFinite(seconds)) {
        return Math.max(0, seconds * 1000);
      }

      const timestamp = Date.parse(value);
      if (Number.isNaN(timestamp)) {
        return null;
      }

      return Math.max(0, timestamp - Date.now());
    },
    showRateLimitNotification(retryDelayMs) {
      if (this.isNotified) {
        return;
      }

      this.isNotified = true;
      const notificationTime = Math.max(5000, Math.min(15000, retryDelayMs));
      this.$q.notify({
        progress: true,
        message: this.$t('error.uploadsRestriction'),
        color: 'info',
        icon: 'info',
        position: 'top-right',
        timeout: notificationTime,
      });

      if (this.rateLimitNotificationTimer) {
        clearTimeout(this.rateLimitNotificationTimer);
      }
      this.rateLimitNotificationTimer = setTimeout(() => {
        this.isNotified = false;
        this.rateLimitNotificationTimer = null;
      }, notificationTime);
    },
    showFailedUploadNotification() {
      const failedCount = this.selectedImage.length;
      const sentCount = Math.max(0, this.imageCount - failedCount);

      this.$q.notify({
        icon: 'error',
        color: 'negative',
        message: this.$t('addMedia.upload.partialFailure', {
          failed: failedCount,
          sent: sentCount,
          total: this.imageCount,
        }),
        timeout: 0,
        position: 'top-right',
        actions: [
          {
            label: this.$t('addMedia.upload.retryFailed', { count: failedCount }),
            color: 'white',
            handler: () => {
              this.startPhotoUpload([...this.selectedImage], true, this._uploadManifest);
            },
          },
          {
            label: this.$t('addMedia.upload.keepFailedForLater', { count: failedCount }),
            color: 'white',
            handler: () => {
              const cloneArray = [...this.selectedImage];
              if (this.$refs.FilePickerRef) {
                this.$refs.FilePickerRef.addFromParent(cloneArray);
              }
              this.exposeCurrentManifestAsPending(true);
              this.step = 1;
              this.isProgress = false;
              this.progress = 0;
              this.uploadStatus = 'idle';
            },
          },
        ],
      });
    },
    async ensureOnline(batch) {
      if (navigator.onLine) {
        return;
      }
      batch.status = 'offline';
      this.refreshUploadStatus();
      await this.waitUntilOnline();
    },
    waitUntilOnline() {
      if (navigator.onLine || this.uploadAbortRequested) {
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        let checkTimer = null;

        const finish = () => {
          window.removeEventListener('online', finish);
          if (checkTimer) {
            clearInterval(checkTimer);
          }
          resolve();
        };

        window.addEventListener('online', finish, { once: true });
        checkTimer = setInterval(() => {
          if (navigator.onLine || this.uploadAbortRequested) {
            finish();
          }
        }, 1000);

        if (navigator.onLine || this.uploadAbortRequested) {
          finish();
        }
      });
    },
    async waitWhilePaused() {
      while (this.uploadPaused && !this.uploadAbortRequested) {
        this.refreshUploadStatus();
        await this.delay(250);
      }
    },
    async delayWithControl(ms) {
      let remaining = Math.max(0, ms);
      while (remaining > 0 && !this.uploadAbortRequested) {
        if (this.uploadPaused) {
          await this.waitWhilePaused();
          continue;
        }
        const step = Math.min(250, remaining);
        await this.delay(step);
        remaining -= step;
      }
    },
    handleBrowserOffline() {
      if (!this.isUploadRunning) {
        return;
      }

      this.logUploadEvent('upload.network.offline', {
        active_requests: this.activeUploadRequests,
      });
      this.uploadBatches.forEach((batch) => {
        if (batch.cancelSource && ['uploading', 'stalled'].includes(batch.status)) {
          batch.cancelSource.cancel('NETWORK_OFFLINE');
        }
        if (
          batch.status !== 'completed' &&
          batch.status !== 'failed' &&
          !['waiting', 'waiting-long'].includes(batch.status)
        ) {
          batch.status = 'offline';
        }
      });
      this.refreshUploadStatus();
    },
    handleBrowserOnline() {
      if (!this.isUploadRunning) {
        return;
      }

      this.logUploadEvent('upload.network.online', {
        confirmed_files: this.confirmedPhotosCount,
      });
      this.uploadBatches.forEach((batch) => {
        if (batch.status === 'offline') {
          batch.status = batch.deferredReason ? 'deferred' : 'retrying';
        }
      });
      this.refreshUploadStatus();
    },
    delay(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    cancelActiveUploads(reason = 'UPLOAD_CANCELLED', includeWaiting = true) {
      this.uploadBatches.forEach((batch) => {
        if (!batch.cancelSource) {
          return;
        }
        if (!includeWaiting && ['waiting', 'waiting-long'].includes(batch.status)) {
          return;
        }
        batch.cancelSource.cancel(reason);
        batch.cancelSource = null;
      });
    },
    cleanupPhotoUploadManager() {
      if (this._uploadManagerCleanupDone) {
        return;
      }
      this._uploadManagerCleanupDone = true;
      this.uploadAbortRequested = true;
      this.isUploadStarting = false;
      this.cancelActiveUploads('UPLOAD_CANCELLED', true);
      if (this.rateLimitNotificationTimer) {
        clearTimeout(this.rateLimitNotificationTimer);
        this.rateLimitNotificationTimer = null;
      }
      if (this.completionResetTimer) {
        clearTimeout(this.completionResetTimer);
        this.completionResetTimer = null;
      }
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      window.removeEventListener('offline', this.handleBrowserOffline);
      window.removeEventListener('online', this.handleBrowserOnline);
    },
  },
};
